import * as React from 'react';
import * as UI from 'core/ui';
import * as Images from 'pos/Images';

import { ProductDetailsDTO, Res, Permission, TicketLocationDTO, ProductTagDTO, AllergenDTO, SaleFormatPriceDTO, CurrentStockDTO } from 'core/api';
import { Command } from 'pos/common';
import { BackActionButton, Content, Footer, Header, PhoneOnly, PosScreen } from 'pos/ui';
import { Result } from 'core/infrastructure';
import { Preconditions } from 'core/infrastructure-next';
import { CatalogueFacade } from 'pos/facades';
import { Message, Modal } from 'core/ui-next/modal';
import { VStack, HStack, Text, Box, Accordion, Image, Separator } from '@chakra-ui/react';
import { SvgIcon } from 'core/ui-next';



type Props = {
	productDetails: ProductDetailsDTO;
} & Modal.Actions<void>;


function ShowProductDetails(props: Props) {

	const { productDetails, cancel } = props;
	const [showImage, setShowImage] = React.useState(true);


	const onCancel = React.useCallback(async () => {
		cancel();
	}, [cancel]);

	const showTagsBody = (productTags: ProductTagDTO[]) => {
		return <HStack w={'full'} wrap={'wrap'} gapX={'4'} gapY={'2'}>
			{productTags.length === 0 &&
				<Box justifyContent={'start'}>
					<Text>{Res.Web.PRODUCT_DETAILS_WITHOUT_TAGS_LBL()}</Text>
				</Box>
			}
			{productTags.map((tag, index) => {
				const imageSrc = tag.ImageContent.length ? 'data:image/*;base64,' + tag.ImageContent : '';

				return (
					<HStack key={index}>
						<Image src={imageSrc} w={'6'} h={'6'} />
						<Box>
							<Text fontSize={'sm'}>{tag.Name}</Text>
						</Box>
					</HStack>);
			})}
		</HStack>;
	};


	const showAllergensBody = (allergens: AllergenDTO[]) => {
		return <HStack w={'full'} wrap={'wrap'} gapX={'4'} gapY={'2'}>
			{allergens.length === 0 &&
				<Box justifyContent={'start'}>
					<Text>{Res.Web.PRODUCT_DETAILS_WITHOUT_ALLERGENS_LBL()}</Text>
				</Box>
			}
			{allergens.map((allergen, index) => {
				const imageSrc = allergen.ImageContent.length ? 'data:image/*;base64,' + allergen.ImageContent : '';
				return (
					<HStack key={index}>
						<Image src={imageSrc} w={'6'} h={'6'} />
						<Box>
							<Text fontSize={'sm'}>{allergen.Name}</Text>
						</Box>
					</HStack>);
			})}
		</HStack>;
	};

	const showPricesBody = (prices: SaleFormatPriceDTO[]) => {
		return <VStack w={'full'} gapY={'2'}>
			{prices.map((price, index) => {
				return (
					<VStack key={index} w={'full'} paddingY={'2'}>
						<HStack w={'full'} justifyContent={'start'}>
							<Text fontSize={'sm'} fontWeight={'semibold'}>{price.PriceListName}</Text>
						</HStack>
						<HStack w={'full'} justifyContent={'space-between'}>
							<Text fontSize={'sm'}>{Res.Web.PRODUCT_DETAILS_MAIN_PRICE_LBL()}</Text>
							<Text fontSize={'sm'}>{UI.Format.asMoney(price.MainPrice)}</Text>
						</HStack>
						<HStack w={'full'} justifyContent={'space-between'}>
							<Text fontSize={'sm'}>{Res.Web.PRODUCT_DETAILS_ADDIN_PRICE_LBL()}</Text>
							<Text fontSize={'sm'}>{UI.Format.asMoney(price.AddinPrice)}</Text>
						</HStack>
						<HStack w={'full'} justifyContent={'space-between'}>
							<Text fontSize={'sm'}>{Res.Web.PRODUCT_DETAILS_MENU_ITEM_PRICE_LBL()}</Text>
							<Text fontSize={'sm'}>{UI.Format.asMoney(price.MenuItemPrice)}</Text>
						</HStack>
						<Separator w={'full'} borderColor={{ base: 'neutral.300', _dark: 'white' }} />
					</VStack>
				);
			})}
		</VStack>;
	};

	const showStockBody = (stock: CurrentStockDTO[]) => {
		return <VStack w={'full'} gapY={'2'}>
			{stock.length === 0 &&
				<Box justifyContent={'start'}>
					<Text fontSize={'sm'}>{Res.Web.PRODUCT_DETAILS_WITHOUT_STOCK_LBL()}</Text>
				</Box>
			}
			{stock.map((s, index) => {
				return (
					<VStack key={index} w={'full'} paddingY={'2'}>
						<HStack w={'full'} justifyContent={'space-between'}>
							<Text fontSize={'sm'} w={'40'} maxW={'40'} minW={'40'} lineClamp={'2'}>{s.Location === '' ? s.WarehouseName : s.WarehouseName + '/' + s.Location}</Text>
							<Text fontSize={'sm'} w={'22.5'} maxW={'22.5'}>{s.Color.Id !== 0 && s.Size.Id !== 0 ? `${s.Size.Name} / ${s.Color.Name}` : ''}</Text>
							<Text fontSize={'sm'}>{s.Stock}</Text>
						</HStack>
					</VStack>
				);
			})}
		</VStack>;
	};

	const showStockInOtherWarehousesBody = (stock: CurrentStockDTO[]) => {
		return <VStack w={'full'} gapY={'2'}>
			{stock.length === 0 &&
				<Box w={'full'} justifyContent={'start'}>
					<Text fontSize={'sm'}>{Res.Web.PRODUCT_DETAILS_WITHOUT_STOCK_LBL()}</Text>
				</Box>
			}
			{stock.map((s) => {
				return (
					<VStack key={s.WarehouseId} w={'full'} paddingY={'2'}>
						<HStack w={'full'} justifyContent={'space-between'}>
							<Text fontSize={'sm'} w={'40'} maxW={'40'} minW={'40'} lineClamp={'2'}>{s.Location === '' ? s.WarehouseName : s.WarehouseName + '/' + s.Location}</Text>
							<Text fontSize={'sm'} w={'22.5'} maxW={'22.5'}>{s.Color.Id !== 0 && s.Size.Id !== 0 ? `${s.Size.Name} / ${s.Color.Name}` : ''}</Text>
							<Text fontSize={'sm'}>{s.Stock}</Text>
						</HStack>
					</VStack>
				);
			})}
		</VStack>;
	};


	const accordionItems = [
		{ value: 'showTags', icon: Images.Tag, title: Res.Web.PRODUCT_DETAILS_TAGS_LBL(), body: showTagsBody(productDetails.Tags) },
		{ value: 'showAllergens', icon: Images.Warning, title: Res.Web.PRODUCT_DETAILS_ALLERGENS_LBL(), body: showAllergensBody(productDetails.Allergens) },
		{ value: 'showPrices', icon: Images.ConfigurePriceList, title: Res.Web.PRODUCT_DETAILS_PRICES_LBL(), body: showPricesBody(productDetails.Prices) },
		{ value: 'showCurrentStock', icon: Images.ProductAvailability, title: Res.Web.PRODUCT_DETAILS_CURRENT_STOCK_LBL(), body: showStockBody(productDetails.CurrentStock) },
		{ value: 'showStockInOtherWarehouses', icon: Images.Warehouse, title: Res.Web.PRODUCT_DETAILS_STOCK_IN_OTHER_WAREHOUSES_LBL(), body: showStockInOtherWarehousesBody(productDetails.StockInOtherWarehouses) }
	];


	const hasProductImage = Boolean(productDetails.DataSheet.ImageContent.length > 0);
	const hasDescription = Boolean(productDetails.DataSheet.Text.length > 0);


	return <PosScreen screenId='SHOW_PRODUCT_DETAILS' size='small' useKeyPad={false} >
		<Header title={Res.Web.SHOW_PRODUCT_DETAILS()} image={Images.ProductDataSheet} onClose={onCancel} />
		<Content>
			<VStack w={'full'} h={'full'} paddingY={'4'} paddingX={'2'} gap={'4'}>
				<HStack w={'full'} maxH={'6'} justifyContent={'space-between'}>
					<Box w={'70'} textAlign={'start'}>
						<Text fontSize={'md'} fontWeight={'semibold'}>{productDetails.ProductName}</Text>
					</Box>
					{hasProductImage && <SvgIcon src={showImage ? Images.ShowImage : Images.HideImage} w={'6'} h={'6'} onClick={() => setShowImage(!showImage)} />}
				</HStack>
				{hasProductImage &&
					<HStack>
						{showImage && <Image src={Images.imgSrcFromDataSheetId(productDetails.DataSheet.ImageId)} w={'90'} h={'42.5'} rounded={'md'} />}
					</HStack>
				}
				{hasDescription &&
					<Box w={'full'} textAlign={'start'}>
						<Text fontSize={'sm'}>{productDetails.DataSheet.Text}</Text>
					</Box>
				}
				<VStack w={'full'} overflowY={'auto'} maxH={'full'}>
					<Accordion.Root w={'full'} rounded={'md'} gapX={'2'} collapsible multiple>
						{accordionItems.map((item, index) => {
							return (
								<Accordion.Item key={index} value={item.value}>
									<Accordion.ItemTrigger paddingX={'2'}>
										<HStack flex="1" gap={'2'}>
											<SvgIcon src={item.icon} w={'6'} h={'6'} /><Text fontSize={'sm'} fontWeight={'semibold'}>{item.title}</Text>
										</HStack>
										<Accordion.ItemIndicator />
									</Accordion.ItemTrigger>
									<Accordion.ItemContent>
										<Accordion.ItemBody paddingX={'2'}>{item.body}</Accordion.ItemBody>
									</Accordion.ItemContent>
								</Accordion.Item>
							);
						})}
					</Accordion.Root>
				</VStack>
			</VStack>
		</Content>
		<Footer>
			<PhoneOnly>
				<BackActionButton action={onCancel} />
			</PhoneOnly>
		</Footer>
	</PosScreen>;
}


interface ProductDetailsParams {
	SaleFormatId: number;
}

declare global {
	interface CommandType {
		showProductDetails: ({ SaleFormatId }: ProductDetailsParams, location: TicketLocationDTO) => Promise<Result>
	}
}

Command.register('showProductDetails', Preconditions.ensure(Permission.Product_ShowDetails), async ({ SaleFormatId }: ProductDetailsParams, location: TicketLocationDTO): Promise<Result> => {

	try {
		const details = await CatalogueFacade.loadProductDetails(location.SaleCenterId, SaleFormatId);

		return await Modal.show(ShowProductDetails, { productDetails: { ...details, CurrentStock: details.CurrentStock.filter(x => x) } });
	}
	catch (error: any) {
		await Message.show(Res.Msg.MSG_PRODUCT_DETAILS_NOT_FOUND(error.message));
		return Result.error();
	}
});
